import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
    Box,
    Button,
    TextField,
    Typography,
    InputAdornment,
    Alert,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import Api from "../services/apis/setup";

type SignUpFormProps = {
    handleActiveFormChange: (form: "signin" | "signup") => void;
};

export default function SignUpForm({ handleActiveFormChange }: SignUpFormProps) {
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isSubmitting },
    } = useForm();
    const [signUp] = Api.useSignUpMutation();
    const password = watch("password");
    const [apiError, setApiError] = useState<string | null>(null);
    const onSubmit = async (data: any) => {
        if (data.password !== data.confirmPassword) {
            alert("Passwords do not match");
            return;
        }
        const response = await signUp({
            baseURL: "/auth/signup",
            payload: JSON.stringify({
                name: data.name,
                email: data.email,
                password: data.password,
            }),
            arguments: {
                name: data.name,
                email: data.email,
                password: data.password,
            },
        });
        if (response?.data?.success) {
            handleActiveFormChange("signin");
        } else {
            // 👇 Show error message
            setApiError(response?.data?.message || "Login failed");
        }
    };

    return (
        <Box>
            <Typography variant="h5" align="center" gutterBottom>
                Create Account
            </Typography>

            <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
                {/* API Error */}
                {apiError && <Alert severity="error">{apiError}</Alert>}
                
                {/* Name */}
                <TextField
                    label="Full Name"
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <PersonIcon />
                            </InputAdornment>
                        ),
                    }}
                    {...register("name", { required: "Name is required" })}
                    error={!!errors.name}
                    helperText={<>{errors?.name?.message ?? ''}</>}
                />

                {/* Email */}
                <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailIcon />
                            </InputAdornment>
                        ),
                    }}
                    {...register("email", {
                        required: "Email is required",
                        pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Enter a valid email address",
                        },
                    })}
                    error={!!errors.email}
                    helperText={<>{errors.email?.message ?? ''}</>}
                />

                {/* Password */}
                <TextField
                    label="Password"
                    type="password"
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon />
                            </InputAdornment>
                        ),
                    }}
                    {...register("password", {
                        required: "Password is required",
                        minLength: {
                            value: 6,
                            message: "Password must be at least 6 characters",
                        },
                    })}
                    error={!!errors.password}
                    helperText={<>{errors.password?.message ?? ''}</>}
                />

                {/* Confirm Password */}
                <TextField
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon />
                            </InputAdornment>
                        ),
                    }}
                    {...register("confirmPassword", {
                        required: "Please confirm your password",
                        validate: (value) =>
                            value === password || "Passwords do not match",
                    })}
                    error={!!errors.confirmPassword}
                    helperText={<>{errors.confirmPassword?.message ?? ''}</>}
                />

                {/* Submit */}
                <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{ backgroundColor: "var(--primary-color)" }}
                >
                    {isSubmitting ? "Signing up..." : "Sign Up"}
                </Button>

                {/* Already have account */}
                <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                    Already have an account?{" "}
                    <Typography
                        variant="overline"
                        onClick={() => handleActiveFormChange("signin")}
                        style={{
                            color: "var(--primary-color)",
                            textDecoration: "none",
                            cursor: "pointer",
                        }}
                    >
                        Sign In
                    </Typography>
                </Typography>
            </Box>
        </Box>
    );
}